import React from "react"
import { useTranslation } from "react-i18next"

import { times } from "shared/lib/times"
import { useEvent } from "shared/lib/useEvent"
import { FilePreview } from "shared/types/File"
import { SelectOption } from "shared/types/Select"
import { APIStatus } from "shared/ui/api-status/APIStatus"
import { FileUpload } from "shared/ui/file-upload/FileUpload"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { ProjectAttachmentsModel } from "./ProjectAttachments"

interface ProjectAttachmentsViewModel extends ProjectAttachmentsModel {}

export const ProjectAttachmentsView = React.memo(
  ({
    files,
    categories,
    acceptsByCategory,
    filesByCategory,
    isLoading,
    isError,
    ...rest
  }: ProjectAttachmentsViewModel) => {
    const { t } = useTranslation()

    if (isLoading) {
      return (
        <div className="project-attachments loading">
          {times(3, index => (
            <div
              key={`attachment-category-${index}`}
              className="project-attachments__category"
            >
              <div className="project-attachments__category-name h6">
                <Skeleton />
              </div>

              <FileUpload.Root
                className="project-attachments__attachment-list"
                isLoading
                files={null}
              >
                <FileUpload.List />
                <FileUpload.Input />
              </FileUpload.Root>
            </div>
          ))}
        </div>
      )
    }

    if (!categories || isError) {
      return (
        <APIStatus
          isUnavailable
          unavailableMessage={t("Attachments.ServiceUnavailable")}
        />
      )
    }

    return (
      <div className="project-attachments">
        {categories?.map(category => (
          <ProjectAttachmentCategory
            {...rest}
            accept={acceptsByCategory[category.value]}
            key={category.value}
            category={category}
            files={filesByCategory[category.value]}
          />
        ))}
      </div>
    )
  }
)

interface ProjectAttachmentCategoryProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof FileUpload.Root>,
    "onUpload"
  > {
  category: SelectOption<string>
  onUpload?: ProjectAttachmentsViewModel["onUpload"]
}

export const ProjectAttachmentCategory = React.memo(
  ({ category, onUpload, ...rest }: ProjectAttachmentCategoryProps) => {
    const handleUpload = useEvent((file: FilePreview) => {
      return onUpload?.(file.file, category.value)
    })

    return (
      <div className="project-attachments__category">
        <h3 className="project-attachments__category-name h6">
          {category.label}
        </h3>

        <FileUpload.Root
          className="project-attachments__attachment-list"
          {...rest}
          onUpload={handleUpload}
        >
          <FileUpload.List />
          <FileUpload.Input variant="minimal" />
        </FileUpload.Root>
      </div>
    )
  }
)
